// =============================
// Email: info@ebenmonney.com
// www.ebenmonney.com/templates
// =============================

import { Component, OnInit, AfterViewInit, TemplateRef, ViewChild, Input, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { fadeInOut } from '../services/animations';
import { AlertService, DialogType, MessageSeverity } from '../services/alert.service';
import { AppTranslationService } from "../services/app-translation.service";
import { AccountService } from "../services/account.service";
import { Utilities } from "../services/utilities";
import { User } from '../models/user.model';
import { Role } from '../models/role.model';
import { Permission } from '../models/permission.model';
import { EditUserDialogComponent } from './edit-user-dialog.component';

@Component({
    selector: 'app-user-list',
    templateUrl: './user-list.component.html',
    styleUrls: ['./user-list.component.scss'],
    animations: [fadeInOut],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserListComponent implements OnInit, AfterViewInit {
    displayedColumns = ['fullName', 'userName', 'isEnabled'];
    dataSource: MatTableDataSource<User>;
    sourceUser: User | null = null;
    loadingIndicator = false;
    allRoles: Role[] = [];

    @ViewChild(MatPaginator, { static: true }) 
    paginator!: MatPaginator;
    @ViewChild(MatSort, { static: true }) 
    sort!: MatSort;

    constructor(
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private accountService: AccountService,
        private snackBar: MatSnackBar,
        private dialog: MatDialog) {


        if (this.canManageUsers)
            this.displayedColumns.push('actions');

        // Assign the data to the data source for the table to render
        this.dataSource = new MatTableDataSource();
    }

    ngOnInit() {
        this.loadData();
    }

    ngAfterViewInit() {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
    }

    public applyFilter(filterValue: string) {
        this.dataSource.filter = filterValue;
    }

    private refresh() {
        // Causes the filter to refresh there by updating with recently added data.
        this.applyFilter(this.dataSource.filter);
    }

    private updateUsers(user: User) {
        if (this.sourceUser) {
            Object.assign(this.sourceUser, user);
            this.alertService.showMessage("OK", `Ändringar för \"${user.userName}\" sparades`, MessageSeverity.success);
            this.sourceUser = null;
        }
        else {
            this.dataSource.data.push(user);
            this.refresh();
            this.alertService.showMessage("OK", `Användare \"${user.userName}\" skapades`, MessageSeverity.success);
        }
    }

    private loadData() {
        this.alertService.startLoadingMessage();
        this.loadingIndicator = true;

        if (this.canViewRoles) {
            this.accountService.getUsersAndRoles().subscribe({
                next: results => this.onDataLoadSuccessful(results[0], results[1]),
                error: error => this.onDataLoadFailed(error)
        });
        }
        else {
            this.accountService.getUsers().subscribe(
                users => this.onDataLoadSuccessful(users, this.accountService.currentUser.roles.map(r => new Role(r))),
                error => this.onDataLoadFailed(error)
            );
        }
    }

    private onDataLoadSuccessful(users: User[], roles: Role[]) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;
        this.dataSource.data = users;
        this.allRoles = roles;
    }

    private onDataLoadFailed(error: any) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        this.alertService.showStickyMessage(this.translationService.getTranslation("Error"), this.translationService.getTranslation("FetchErrorMessage") + "\r\n" + Utilities.getHttpResponseMessage(error),
            MessageSeverity.error, error);
    }

    public editUser(user?: User) {

        // Reload individual user to get region data included
        if (user != null) {
            this.accountService.getUserAndRoles(user.id).subscribe({
                next: usr => {
                    user = usr[0];
                    this.sourceUser = usr[0];
                    this.openEditUserDialog(user);
                },
                error: error => {
                    this.alertService.stopLoadingMessage();
                    this.alertService.showStickyMessage(error, null, MessageSeverity.error);
                }
            });
        }
        else {
            this.sourceUser = user ?? null;
            this.openEditUserDialog(user);
        }
    }

    openEditUserDialog(user?: User) {
        let dialogRef = this.dialog.open(EditUserDialogComponent,
            {
                minWidth: '80vw',
                data: { user: user, roles: [...this.allRoles] }
            });
        dialogRef.afterClosed().subscribe(user => {
            if (user) {
                this.updateUsers(user);
            }
        });
    }

    public confirmDelete(user: User) {
        let message: string = this.translationService.getTranslation("commands.Delete") +
            " " +
            user.fullName +
            "?";
        let action: string = this.translationService.getTranslation("commands.Delete").toUpperCase();

        this.snackBar.open(message, action, { duration: 5000 })
            .onAction().subscribe(() => {
                this.alertService.startLoadingMessage(
                    this.translationService.getTranslation("globalTexts.DeletingMessage"));
                this.loadingIndicator = true;

                this.accountService.deleteUser(user)
                    .subscribe({
                        next: () => {
                            this.alertService.stopLoadingMessage();
                            this.loadingIndicator = false;
                            this.alertService.showMessage(this.translationService.getTranslation("commands.Delete"),
                                user.fullName + " " + this.translationService.getTranslation("globalTexts.deleted"),
                                MessageSeverity.success);
                            this.loadData();
                        },
                        error: error => {
                            this.alertService.stopLoadingMessage();
                            this.loadingIndicator = false;

                            let errorMessage = this.translationService.getTranslation("globalTexts.Error") +
                                ": " +
                                Utilities.getHttpResponseMessage(error);

                            this.alertService.showStickyMessage(
                                this.translationService.getTranslation("globalTexts.DeleteError"),
                                errorMessage,
                                MessageSeverity.error,
                                error);
                            }
                    });
            });
    }

    get canManageUsers() {
        return this.accountService.userHasPermission(Permission.manageUsersPermission);
    }

    get canViewRoles() {
        return this.accountService.userHasPermission(Permission.viewRolesPermission);
    }

    get canAssignRoles() {
        return this.accountService.userHasPermission(Permission.assignRolesPermission);
    }
}
